import React, { useEffect, useState, useMemo } from 'react';
import { Drawer, Alert, Button, FormRow, DateRangePicker, GroupedCheckboxDropdown } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import Select from 'react-select';
import PropTypes from 'prop-types';
import { MdSearch, MdOutlineRestartAlt, MdOutlineClose, MdFilter } from 'react-icons/md';
import { setNestedParams, resetNestedParams, setFilterTag, getSalesPipelines } from 'redux/crm/projectSlice';
import { getActiveSalesUnits } from 'redux/crm/salesUnitSlice';
import { getPublicUsersForFilter } from 'redux/authorization/userSlice';
import EmployeeSelectField from 'components/common/select/EmployeeSelectField';
import { makeEmployeeLoaderWithDetails } from 'helpers/debounce';
import SalesUnitGroupedDropdown from 'components/app/crm/shared/SalesUnitGroupedDropdown';
import { INPUT_CLS, SELECT_STYLES } from 'helpers/formStyles';

const PROJECT_TYPE_OPTIONS = [
  { value: 'hot_roll', label: 'Hot Roll' },
  { value: 'built_up', label: 'Built Up' },
];

const FilterAdvanceModal = ({ showModal, setshowModal, data, entryKey, onApply = null, onReset = null }) => {
  const { activeSalesUnitsData } = useSelector(state => state.crm_sales_unit);
  const { activeData }           = useSelector(state => state.authorization_user);
  const { salesPipelines = [] }  = useSelector(state => state.crm_project);
  const { control, register, handleSubmit, setValue, reset } = useForm();
  const dispatch = useDispatch();

  const { pageSize } = data || {};
  const key = entryKey;

  const [createdDateRange,     setCreatedDateRange]     = useState({ startDate: '', endDate: '' });
  const [openDateRange,        setOpenDateRange]        = useState({ startDate: '', endDate: '' });
  const [closedDateRange,      setClosedDateRange]      = useState({ startDate: '', endDate: '' });
  const [selectedPipelineIds,  setSelectedPipelineIds]  = useState([]);
  const [selectedSalesUnitIds, setSelectedSalesUnitIds] = useState([]);
  const salesEmployeeLoader = useMemo(() => makeEmployeeLoaderWithDetails('sales'), []);

  useEffect(() => {
    dispatch(getActiveSalesUnits());
    dispatch(getPublicUsersForFilter({ per_page: 500 }));
    dispatch(getSalesPipelines());
  }, [dispatch]);

  const pipelineGroups = useMemo(() => {
    const groupMap = {};
    salesPipelines.forEach((sp) => {
      const groupKey = sp.group_name || 'Other';
      if (!groupMap[groupKey]) {
        groupMap[groupKey] = { key: groupKey, label: groupKey, color: sp.color, items: [] };
      }
      groupMap[groupKey].items.push({ value: sp.id, label: sp.name, color: sp.color });
    });
    return Object.values(groupMap);
  }, [salesPipelines]);

  const userOptions   = (activeData?.data || []).map(u => ({ value: u.id, label: u.name || u.email, name: u.name }));

  const closeModal = () => setshowModal(false);

  const onSubmit = (formData) => {
    const createdByNames = (formData.created_by_ids || [])
      .map(id => userOptions.find(u => u.value === id)?.label)
      .filter(Boolean);

    const pipelineNames = selectedPipelineIds
      .map(id => salesPipelines.find(sp => sp.id === id)?.name)
      .filter(Boolean);

    const newData = {
      id_cont:                     formData.pcv_number,
      name_cont:                   formData.project_name,
      q_number_cont:               formData.q_number,
      j_number_cont:               formData.j_number,
      company_name_cont:           formData.customer_name,
      enduser_customer_name_cont:  formData.enduser_name,
      pj_location_cont:            formData.project_location,
      sales_incharge_cont:         formData.sales_incharge,
      user_id_in:                  formData.created_by_ids,
      project_created_at_gteq:     createdDateRange.startDate || undefined,
      project_created_at_lteq:     createdDateRange.endDate   || undefined,
      open_date_gteq:              openDateRange.startDate    || undefined,
      open_date_lteq:              openDateRange.endDate      || undefined,
      closed_date_gteq:            closedDateRange.startDate  || undefined,
      closed_date_lteq:            closedDateRange.endDate    || undefined,
      project_type_eq:             formData.project_type === 'hot_roll' ? 2
                                 : formData.project_type === 'built_up' ? 1
                                 : undefined,
      status_id_in:                formData.status_ids,
      sales_unit_id_in:            selectedSalesUnitIds.length ? selectedSalesUnitIds : undefined,
      sales_pipeline_id_in:        selectedPipelineIds.length ? selectedPipelineIds : undefined,
      page:     1,
      per_page: pageSize,
    };

    const dataFilter = [
      { id: 'id_cont',                    name: 'PCV number',      value: formData.pcv_number },
      { id: 'name_cont',                  name: 'Project name',    value: formData.project_name },
      { id: 'q_number_cont',              name: 'Quote number',    value: formData.q_number },
      { id: 'j_number_cont',              name: 'Job number',      value: formData.j_number },
      { id: 'company_name_cont',          name: 'Customer name',   value: formData.customer_name },
      { id: 'enduser_customer_name_cont', name: 'End user name',   value: formData.enduser_name },
      { id: 'pj_location_cont',           name: 'Project location',value: formData.project_location },
      { id: 'sales_incharge_cont',        name: 'Sales incharge',  value: formData.sales_incharge },
      { id: 'project_type_eq',            name: 'Project type',
        value: formData.project_type === 'hot_roll' ? 'Hot Roll'
             : formData.project_type === 'built_up' ? 'Built Up'
             : '' },
      { id: 'user_id_in',                 name: 'Created by',      value: createdByNames.length ? createdByNames : undefined },
      { id: 'status_id_in',               name: 'Status',          value: formData.status_ids },
      { id: 'sales_unit_id_in',           name: 'Sales unit',      value: selectedSalesUnitIds.length ? selectedSalesUnitIds : undefined },
      { id: 'sales_pipeline_id_in',       name: 'Sales Pipeline',  value: pipelineNames.length ? pipelineNames : undefined },
    ];

    if (createdDateRange.startDate && createdDateRange.endDate) {
      dataFilter.push({ id: 'project_created_at_gteq', name: 'Created date', value: [createdDateRange.startDate, createdDateRange.endDate] });
    }
    if (openDateRange.startDate && openDateRange.endDate) {
      dataFilter.push({ id: 'open_date_gteq', name: 'Open date', value: [openDateRange.startDate, openDateRange.endDate] });
    }
    if (closedDateRange.startDate && closedDateRange.endDate) {
      dataFilter.push({ id: 'closed_date_gteq', name: 'Closed date', value: [closedDateRange.startDate, closedDateRange.endDate] });
    }

    const tags = dataFilter.filter(d => d.value !== undefined && d.value !== '' && !(Array.isArray(d.value) && d.value.length === 0));

    const params = Object.fromEntries(
      Object.entries(newData).filter(([, v]) => v !== null && v !== '' && v !== undefined)
    );

    try {
      if (onApply) {
        onApply(params, tags);
      } else {
        dispatch(setFilterTag({ key, tags }));
        dispatch(setNestedParams({ key, params }));
        closeModal();
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleReset = () => {
    reset();
    setCreatedDateRange({ startDate: '', endDate: '' });
    setOpenDateRange({ startDate: '', endDate: '' });
    setClosedDateRange({ startDate: '', endDate: '' });
    setSelectedPipelineIds([]);
    setSelectedSalesUnitIds([]);
    setValue('project_type', null);
    setValue('sales_incharge', null);
    setValue('created_by_ids', null);
    if (onReset) {
      onReset();
    } else {
      dispatch(resetNestedParams({ key }));
    }
  };

  const footer = (
    <>
      <Button type="submit" size="sm" onClick={handleSubmit(onSubmit)}>
        <MdSearch size={14} /> Search
      </Button>
      <Button variant="danger" size="sm" onClick={handleReset}>
        <MdOutlineRestartAlt size={14} /> Reset
      </Button>
      <Button variant="ghost" size="sm" onClick={closeModal}>
        <MdOutlineClose size={14} /> Close
      </Button>
    </>
  );

  return (
    <Drawer
      show={showModal}
      onHide={closeModal}
      title={<span className="tw-flex tw-items-center tw-gap-1.5"><MdFilter size={15} /> Apply filters</span>}
      width="40%"
      backdropClick={false}
      footer={footer}
    >
      <Alert type="info" message="Establish the project's filtering criteria." className="tw-mb-4" />

      <FormRow label="PCV number">
        <input type="text" placeholder="PCV number" className={INPUT_CLS} {...register('pcv_number')} />
      </FormRow>

      <FormRow label="Quote number">
        <input type="text" placeholder="Quote number" className={INPUT_CLS} {...register('q_number')} />
      </FormRow>

      <FormRow label="P/J number">
        <input type="text" placeholder="P/J number" className={INPUT_CLS} {...register('j_number')} />
      </FormRow>

      <FormRow label="Project name">
        <input type="text" placeholder="Project name" className={INPUT_CLS} {...register('project_name')} />
      </FormRow>

      <FormRow label="Project location">
        <input type="text" placeholder="Project location" className={INPUT_CLS} {...register('project_location')} />
      </FormRow>

      <FormRow label="Sales incharge">
        <Controller
          control={control}
          name="sales_incharge"
          render={({ field }) => (
            <EmployeeSelectField
              name={field.name}
              control={control}
              loadOptions={salesEmployeeLoader}
              placeholder="Select sales incharge..."
              defaultOptions={true}
              defaultValue={field.value ? { value: field.value, label: field.value } : null}
            />
          )}
        />
      </FormRow>

      <FormRow label="Project type">
        <Controller
          control={control}
          name="project_type"
          render={({ field }) => (
            <Select
              isClearable
              options={PROJECT_TYPE_OPTIONS}
              placeholder="All (default)"
              menuPosition="fixed"
              styles={SELECT_STYLES}
              value={PROJECT_TYPE_OPTIONS.find(o => o.value === field.value) || null}
              onChange={opt => setValue('project_type', opt ? opt.value : null)}
            />
          )}
        />
      </FormRow>

      <div className="tw-border-t tw-border-dashed tw-border-slate-200 tw-my-4" />

      <FormRow label="Customer name">
        <input type="text" placeholder="Customer name" className={INPUT_CLS} {...register('customer_name')} />
      </FormRow>

      <FormRow label="Enduser name">
        <input type="text" placeholder="Enduser name" className={INPUT_CLS} {...register('enduser_name')} />
      </FormRow>

      <div className="tw-border-t tw-border-dashed tw-border-slate-200 tw-my-4" />

      <FormRow label="Sales unit">
        <SalesUnitGroupedDropdown
          units={activeSalesUnitsData || []}
          selectedIds={selectedSalesUnitIds}
          onChange={setSelectedSalesUnitIds}
          placeholder="All units"
          width={340}
        />
      </FormRow>

      <FormRow label="Sales Pipeline">
        <GroupedCheckboxDropdown
          groups={pipelineGroups}
          selectedIds={selectedPipelineIds}
          onChange={setSelectedPipelineIds}
          placeholder="All pipelines"
          width={340}
        />
      </FormRow>

      <div className="tw-border-t tw-border-dashed tw-border-slate-200 tw-my-4" />

      <FormRow label="Open date">
        <DateRangePicker
          label="Open date"
          startDate={openDateRange.startDate}
          endDate={openDateRange.endDate}
          onChange={setOpenDateRange}
        />
      </FormRow>

      <FormRow label="Closed date">
        <DateRangePicker
          label="Closed date"
          startDate={closedDateRange.startDate}
          endDate={closedDateRange.endDate}
          onChange={setClosedDateRange}
        />
      </FormRow>

      <div className="tw-border-t tw-border-dashed tw-border-slate-200 tw-my-4" />

      <FormRow label="Created by">
        <Controller
          control={control}
          name="created_by_ids"
          render={({ field }) => (
            <Select
              isMulti
              isSearchable
              closeMenuOnSelect={false}
              options={userOptions}
              placeholder="Select users..."
              menuPosition="fixed"
              styles={SELECT_STYLES}
              value={field.value ? userOptions.filter(o => field.value.includes(o.value)) : []}
              onChange={opts => setValue('created_by_ids', opts.map(o => o.value), { shouldValidate: true })}
            />
          )}
        />
      </FormRow>

      <FormRow label="Created date">
        <DateRangePicker
          label="Created date"
          startDate={createdDateRange.startDate}
          endDate={createdDateRange.endDate}
          onChange={setCreatedDateRange}
        />
      </FormRow>
    </Drawer>
  );
};

FilterAdvanceModal.propTypes = {
  showModal:    PropTypes.bool.isRequired,
  setshowModal: PropTypes.func.isRequired,
  data:         PropTypes.object.isRequired,
  entryKey:     PropTypes.string.isRequired,
  onApply:      PropTypes.func,
  onReset:      PropTypes.func,
};

export default FilterAdvanceModal;
